import React, { useState, useRef, useEffect, useCallback } from 'react';
import jsQR from 'jsqr';
import { XMarkIcon, CameraIcon } from '@heroicons/react/24/outline';

interface QRCodeScannerProps {
  onScan: (code: string) => void;
  onClose: () => void;
}

/** Deep-link prefix the app encodes into friend QR codes. */
const FRIEND_CODE_PREFIX = 'connectshare://add-friend/';

/**
 * Extract the friend code from scanned QR data. Friend codes are encoded as
 * `connectshare://add-friend/<code>` deep links; anything else is returned as-is
 * so a plain code still works.
 */
export const parseFriendCode = (data: string): string => {
  const trimmed = data.trim();
  if (trimmed.startsWith(FRIEND_CODE_PREFIX)) {
    return trimmed.slice(FRIEND_CODE_PREFIX.length).replace(/\/+$/, '');
  }
  return trimmed;
};

/**
 * QRCodeScanner Component
 *
 * Opens the device camera and continuously decodes frames with jsQR. On a
 * successful decode it extracts the friend code (see {@link parseFriendCode}),
 * reports it via `onScan`, and stops the camera. A manual-entry fallback is
 * always available.
 */
const QRCodeScanner: React.FC<QRCodeScannerProps> = ({ onScan, onClose }) => {
  const [error, setError] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Hold the latest onScan without re-creating the camera callbacks (which would
  // restart the stream / re-prompt for permission) when the parent re-renders.
  const onScanRef = useRef(onScan);
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const stopCamera = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setIsScanning(false);
  }, []);

  const startScanning = useCallback(() => {
    // Guard against a leftover loop if startScanning is ever called twice.
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }

    scanIntervalRef.current = setInterval(() => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return;

      const context = canvas.getContext('2d');
      // Zero-dimension frames (during stream init/transitions) would make
      // getImageData throw an IndexSizeError, so skip them.
      if (
        !context ||
        video.readyState !== video.HAVE_ENOUGH_DATA ||
        video.videoWidth === 0 ||
        video.videoHeight === 0
      ) {
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });

      if (code) {
        const parsed = parseFriendCode(code.data);
        // Ignore empty/prefix-only codes and keep scanning.
        if (parsed) {
          stopCamera();
          onScanRef.current(parsed);
        }
      }
    }, 300);
  }, [stopCamera]);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsScanning(true);
        startScanning();
      }
    } catch (err) {
      setError('Failed to access camera. Please ensure camera permissions are granted.');
      console.error('Camera access error:', err);
    }
  }, [startScanning]);

  useEffect(() => {
    startCamera();

    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  const handleManualInput = () => {
    stopCamera();
    const code = prompt('Enter friend code manually:');
    if (code) {
      onScan(code);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-[52] flex items-center justify-center">
      <div className="relative w-full max-w-md mx-4">
        {/* Close Button */}
        <button
          onClick={() => {
            stopCamera();
            onClose();
          }}
          className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 hover:bg-gray-100"
        >
          <XMarkIcon className="h-6 w-6 text-gray-700" />
        </button>

        {/* Camera View */}
        <div className="bg-white rounded-lg overflow-hidden">
          <div className="p-4 bg-primary-600 text-white text-center">
            <CameraIcon className="h-8 w-8 mx-auto mb-2" />
            <h3 className="text-lg font-semibold">Scan Friend Code</h3>
            <p className="text-sm text-blue-100 mt-1">
              Position the QR code within the frame
            </p>
          </div>

          {error ? (
            <div className="p-8 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={handleManualInput}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-200"
              >
                Enter Code Manually
              </button>
            </div>
          ) : (
            <div className="relative">
              {/* Video element */}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-64 object-cover bg-black"
              />

              {/* Hidden canvas for QR code processing */}
              <canvas ref={canvasRef} className="hidden" />

              {/* Scanning overlay */}
              {isScanning && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-48 h-48 border-4 border-primary-500 rounded-lg relative">
                    <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white"></div>
                    <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white"></div>
                    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white"></div>
                    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="p-4 bg-gray-50 text-center">
            <button
              onClick={handleManualInput}
              className="text-sm text-primary-600 hover:text-primary-800 transition-colors duration-200 font-medium"
            >
              Can't scan? Enter code manually
            </button>
          </div>
        </div>

        <p className="text-white text-xs text-center mt-4 opacity-75">
          Note: QR code scanning requires camera permissions
        </p>
      </div>
    </div>
  );
};

export default QRCodeScanner;
